// src/utils/api.js
// ─────────────────────────────────────────────────────────────────────────────
// API service for RS Planning Form
//
// Customer flow
//   GET  https://api-uat.makemyhouse.com/customer/form/:projectId
//   PUT  https://api-uat.makemyhouse.com/customer/form/:projectId
//   POST https://api-uat.makemyhouse.com/customer/form/:projectId  (final — no /store)
//   PUT  https://api-uat.makemyhouse.com/customer/form/moveCardToInProgress/:projectId
//
// Admin flow
//   GET  https://api-uat.makemyhouse.com/internal/form/:projectId
//   PUT  https://api-uat.makemyhouse.com/internal/form/:projectId
//   POST https://api-uat.makemyhouse.com/internal/form/:projectId  (final — no /store)
//   PUT  https://api-uat.makemyhouse.com/internal/form/moveCardToInProgress/:projectId
// ─────────────────────────────────────────────────────────────────────────────

import {
  getCustomerToken,
  getUserIsdCode,
  saveUserIsdCode,
  getUserPhone,
  clearCustomerSession,
} from "./storage";

// const API_BASE = "https://api-uat.makemyhouse.com";
// const API_BASE = "https://api.makemyhouse.com";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

// Build request headers
const getHeaders = (token) => ({
  "Content-Type": "application/json",
  "x-api-key": token || "b1344b0057496b4c7bdc0e9b6ffcce6f",
});

// ─── Direction Mapping ────────────────────────────────────────────────────────
// FIX: purana normalizeDirection sirf EXACT string variants handle karta tha
// (jaise "North-East", "NE"). Agar backend/admin se value "North_East"
// (underscore) ya "North East" (space) ya "northeast" jaisi aati thi, to
// woh silently 8 canonical values ("north-east" etc.) se match nahi hoti
// thi — isi wajah se kuch forms mein Step 2 ki selected direction Step 3
// (aur Step 4) ke compass pe sahi se reflect nahi hoti thi, kuch mein
// (jinki value already canonical format mein saved thi) sahi chal jaati thi.
// Ab niche wala version separators/case ko normalize karta hai, aur numeric
// angle ("45", "180") ko bhi nearest direction mein map kar deta hai.
const OLD_DIRECTION_MAP = {
  "north": "north",
  "south": "south",
  "east": "east",
  "west": "west",
  "north-east": "north-east",
  "north-west": "north-west",
  "south-east": "south-east",
  "south-west": "south-west",
  "nn": "north",
  "ss": "south",
  "ee": "east",
  "ww": "west",
  "ne": "north-east",
  "nw": "north-west",
  "se": "south-east",
  "sw": "south-west",
};

const DIRECTION_TO_CODE = {
  "north": "NN",
  "south": "SS",
  "east": "EE",
  "west": "WW",
  "north-east": "NE",
  "north-west": "NW",
  "south-east": "SE",
  "south-west": "SW",
};

// Clockwise order starting at North — index * 45° = compass angle.
// Used for numeric-angle fallback below.
const CANONICAL_DIRECTIONS_BY_ANGLE = [
  "north", "north-east", "east", "south-east",
  "south", "south-west", "west", "north-west",
];

export const normalizeDirection = (raw) => {
  if (raw === null || raw === undefined) return "";
  const str = String(raw).trim();
  if (!str || str.toLowerCase() === "select direction") return "";

  // 1) Normalize separators + case: "North East" / "North_East" / "NORTH-EAST"
  //    all become "north-east" before we try to match anything.
  const cleaned = str
    .toLowerCase()
    .replace(/[_\s]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  if (OLD_DIRECTION_MAP[cleaned]) return OLD_DIRECTION_MAP[cleaned];

  // 2) Numeric compass angle in degrees (e.g. "45", "180.0") → nearest 45° direction.
  if (/^-?\d+(\.\d+)?$/.test(cleaned)) {
    const angle = ((parseFloat(cleaned) % 360) + 360) % 360;
    const idx = Math.round(angle / 45) % 8;
    return CANONICAL_DIRECTIONS_BY_ANGLE[idx];
  }

  // 3) Still unrecognized — return the cleaned value so the UI degrades
  //    gracefully, but warn so this exact raw value can be added above.
  console.warn(`[normalizeDirection] Unrecognized direction value from API: "${raw}"`);
  return cleaned;
};

export const directionToCode = (dir) => DIRECTION_TO_CODE[normalizeDirection(dir)] ?? dir;


// ─── Floor Mapping ────────────────────────────────────────────────────────────
export const numberToFloors = (num) => {
  const n = parseInt(num);
  if (!n || n <= 1) return "ground";
  return `ground+${n - 1}`;
};

export const floorsToNumber = (floors) => {
  if (!floors) return 1;
  if (floors === "ground") return 1;
  const match = String(floors).match(/ground\+(\d+)/);
  if (match) return 1 + parseInt(match[1]);
  const n = parseInt(floors);
  return isNaN(n) ? 1 : n;
};

// ─── Feet + Inches Helpers ────────────────────────────────────────────────────
export const splitDecimal = (val) => {
  if (val === "" || val === null || val === undefined) return { feet: "", inches: "0" };
  const str = String(val);
  const parts = str.split(".");
  return {
    feet: parts[0] || "",
    inches: parts[1] || "0",
  };
};

export const mergeToDecimal = (feet, inches) => {
  const f = parseFloat(feet) || 0;
  const i = parseFloat(inches) || 0;
  if (f === 0 && i === 0) return "";
  return i > 0 ? `${f}.${i}` : `${f}`;
};

// ─── Zero/Falsy value ko empty string mein convert karo ──────────────────────
// API se 0 aata hai — component mein "" chahiye hota hai
const zeroToEmpty = (val) => {
  if (val === 0 || val === "0" || val === null || val === undefined) return "";
  return String(val);
};

// ─── Stair Type Mapping ───────────────────────────────────────────────────────
const normalizeStairType = (type) => {
  if (!type || type === 0 || type === "0") return "";
  return String(type).toLowerCase();
};

// ─── Parking Mapping ─────────────────────────────────────────────────────────
const normalizeParkingRequirement = (parkingForCar, parkingForBike) => {
  const hasCar = parseInt(parkingForCar) > 0;
  const hasBike = parseInt(parkingForBike) > 0;
  if (hasCar && hasBike) return "both";
  if (hasCar) return "car";
  if (hasBike) return "bike";
  return "none";
};

// ─── Peripheries Mapping ──────────────────────────────────────────────────────
const normalizePeripheryValue = (val) => {
  if (!val) return "";
  const lower = val.toLowerCase();
  if (lower === "road") return "Road";
  if (lower.includes("neighbour") || lower.includes("neighbor")) return "Neighbour Plot";
  return val;
};

// ─── Floor Requirements Mapping ───────────────────────────────────────────────
const OLD_SPACE_TO_NEW = {
  "poojaRoom": "Pooja Room",
  "commonAreaType": null,
  "washArea": "Utility/Wash Area",
  "dressingArea": "Dressing Area",
  "studyRoom": "Study Room",
  "recreationArea": "Recreation Area",
  "garden": "Garden",
  "gym": "Gym Room",
  "theatre": "Theatre Room",
  "office": "Office",
  "foyer": "Foyer",
  "Balcony": "Balcony",
  "lift": "Lift",
  "shop": "Shop",
};

const COMMON_AREA_MAP = {
  "Living Come Dining": "Living Room",
  "Living Room": "Living Room",
  "Drawing Room": "Drawing Room",
  "Dining Area": "Dining Area",
  "Living & Dining": "Living Room",
};

const mapOldFloorToNew = (oldFloor) => {
  if (!oldFloor) return {};

  const additionalSpaces = [];

  // FIX: commonAreaType: 0 aata hai — sirf valid string pe map karo
  const commonAreaRaw = oldFloor.commonAreaType;
  if (commonAreaRaw && commonAreaRaw !== 0 && commonAreaRaw !== "0") {
    const mapped = COMMON_AREA_MAP[commonAreaRaw];
    if (mapped) additionalSpaces.push(mapped);
    if (
      commonAreaRaw === "Living Come Dining" ||
      commonAreaRaw === "Living & Dining"
    ) {
      additionalSpaces.push("Dining Area");
    }
  }

  // Handle new-style true/false keys for Living Room, Dining Area, Drawing Room
  if (oldFloor.livingRoom === "true" || oldFloor.livingRoom === true) {
    if (!additionalSpaces.includes("Living Room")) additionalSpaces.push("Living Room");
  }
  if (oldFloor.diningArea === "true" || oldFloor.diningArea === true) {
    if (!additionalSpaces.includes("Dining Area")) additionalSpaces.push("Dining Area");
  }
  if (oldFloor.drawingRoom === "true" || oldFloor.drawingRoom === true) {
    if (!additionalSpaces.includes("Drawing Room")) additionalSpaces.push("Drawing Room");
  }

  Object.entries(OLD_SPACE_TO_NEW).forEach(([oldKey, newLabel]) => {
    if (oldKey === "commonAreaType" || !newLabel) return;
    const val = oldFloor[oldKey];
    if (val === "true" || val === true) {
      additionalSpaces.push(newLabel);
    }
  });

  // kitchen API se 0, null, string kuch bhi aa sakta hai — String() se safe karo
  const kitchenRaw = oldFloor.kitchen != null && oldFloor.kitchen !== 0 && oldFloor.kitchen !== false
    ? String(oldFloor.kitchen)
    : "";
  const kitchenLower = kitchenRaw.toLowerCase();
  // Dono format support karo:
  //  - naya:  kitchen = "true"/"false", kitchenType = "open"/"close" (alag key)
  //  - purana: kitchen = "Open"/"Close"/"No"
  const hasKitchen =
    kitchenLower === "true" || kitchenLower === "yes" ||
    kitchenLower === "open" || kitchenLower === "close" || kitchenLower === "closed";
  const kitchenTypeRaw = String(oldFloor.kitchenType ?? "").toLowerCase();
  const kitchenType =
    kitchenTypeRaw === "open"
      ? "open"
      : (kitchenTypeRaw === "close" || kitchenTypeRaw === "closed")
        ? "close"
        : kitchenLower === "open"   // purana format fallback
          ? "open"
          : "close";

  const masterBedrooms = oldFloor.masterBedrooms || oldFloor.guestBedrooms || "";

  const otherSpaces = oldFloor["AnySpecificRequirement"] || oldFloor["Any Specific Requirement"] || "";

  // withAttached — API se "True"/"False"/true/false/1/0 kuch bhi aaye — boolean banao
  const rawAttached = oldFloor.attachedToilet;
  const withAttached =
    rawAttached === true ||
    rawAttached === 1 ||
    String(rawAttached).toLowerCase() === "true" ||
    String(rawAttached).toLowerCase() === "yes";

  // NEW — masterBedroomsWithToilet + bedroomsWithoutToilet derive karo
  const masterBedroomsWithToilet =
    oldFloor.masterBedroomsWithToilet !== undefined
      ? String(oldFloor.masterBedroomsWithToilet)
      : withAttached ? String(masterBedrooms) : "";

  const bedroomsWithoutToilet =
    oldFloor.bedroomsWithoutToilet !== undefined
      ? String(oldFloor.bedroomsWithoutToilet)
      : !withAttached ? String(masterBedrooms) : "";

  return {
    masterBedrooms: String(masterBedrooms),
    masterBedroomsWithToilet,  // NEW — Step11
    bedroomsWithoutToilet,     // NEW — Step11
    additionalSpaces: [...new Set(additionalSpaces)],
    kitchen: hasKitchen ? "yes" : "no",
    kitchenType: hasKitchen ? kitchenType : "open",
    withAttached,     // strict boolean: true | false
    otherSpaces,
  };
};

// ─── Setbacks → buildingArea polygon ─────────────────────────────────────────
// Step7 model coords: tl/tr = y=0 = BACK, bl/br = y=depth = FRONT
// BuildingPoints order: [0]=tl(back-left), [1]=tr(back-right), [2]=br(front-right), [3]=bl(front-left)
const setbacksToPolygon = (width, depth, front, back, left, right) => {
  const w = parseFloat(width) || 0;
  const d = parseFloat(depth) || 0;
  const f = parseFloat(front) || 0;
  const b = parseFloat(back) || 0;
  const l = parseFloat(left) || 0;
  const r = parseFloat(right) || 0;

  return [
    { x: l, y: d - b },  // [0] tl = back-left
    { x: w - r, y: d - b },  // [1] tr = back-right
    { x: w - r, y: f },  // [2] br = front-right
    { x: l, y: f },  // [3] bl = front-left
  ];
};


const flatKeysToPolygon = (d) => {
  const tlx = d.buildingAreaTopLeftX;
  const tly = d.buildingAreaTopLeftY;
  const trx = d.buildingAreaTopRightX;
  const trY = d.buildingAreaTopRightY;
  const brx = d.buildingAreaBottomRightX;
  const bry = d.buildingAreaBottomRightY;
  const blx = d.buildingAreaBottomLeftX;
  const bly = d.buildingAreaBottomLeftY;

  const isValid = (v) => v !== undefined && v !== null && v !== "" && !isNaN(parseFloat(v));

  if (
    isValid(tlx) && isValid(tly) &&
    isValid(trx) && isValid(trY) &&
    isValid(brx) && isValid(bry) &&
    isValid(blx) && isValid(bly)
  ) {
    return [
      { x: parseFloat(tlx), y: parseFloat(tly) },
      { x: parseFloat(trx), y: parseFloat(trY) },
      { x: parseFloat(brx), y: parseFloat(bry) },
      { x: parseFloat(blx), y: parseFloat(bly) },
    ];
  }
  return null;
};

// ─────────────────────────────────────────────────────────────────────────────
// fromApiFormat: Convert OLD API response → new internal formData
// ─────────────────────────────────────────────────────────────────────────────
export const fromApiFormat = (apiData) => {
  const d = apiData || {};

  // Naya format (add/update dono): width=front, backWidth=back, leftDepth=left, depth=right.
  // frontWidth/rightDepth/plot* sirf purane saved records ke fallback ke liye padhte hain.
  const widthRaw = d.width || d.frontWidth || "";
  const depthRaw = d.depth || d.rightDepth || d.leftDepth || "";

  const widthParts = splitDecimal(widthRaw);
  const depthParts = splitDecimal(depthRaw);

  // Non-rectangular ke liye backWidth aur rightDepth — API me ye keys hongi
  // Agar API me nahi hai to width/depth se hi prefill karo
  const backWidthRaw = d.plotBackWidth || d.backWidth || widthRaw;
  const rightDepthRaw = d.plotRightDepth || d.rightDepth || depthRaw;
  const backWidthParts = splitDecimal(backWidthRaw);
  const rightDepthParts = splitDecimal(rightDepthRaw);

  const direction = normalizeDirection(d.direction);

  const peripheries = {
    left: normalizePeripheryValue(d.inLeft),
    right: normalizePeripheryValue(d.inRight),
    back: normalizePeripheryValue(d.inBack),
  };

  // FIX: API mein "floor" bhi aata hai, "floors" bhi — dono handle karo
  const numberOfFloors = numberToFloors(d.floors || d.floor);

  const floorsData = Array.isArray(d.requirementForfloors)
    ? d.requirementForfloors.map(mapOldFloorToNew)
    : [];

  const parkingRequirement = d.parking
    ? d.parking
    : normalizeParkingRequirement(d.parkingForCar, d.parkingForBike);

  // FIX: staircaseType 0 → "" handle kiya, aur stairType bhi set karo
  // taaki component chahe stair.type se padhe ya stairType se — dono kaam kare
  const stairTypeNormalized = normalizeStairType(d.staircaseType);
  const stair = {
    type: stairTypeNormalized,
    riser: "",
    tread: "",
  };

  const isIrregular = d.shape === "irregular" || d.shape === "non-rectangular";

  // FIX: splitDecimal use karo taaki inches bhi sahi aaye
  // "70.6" → feet="70", inches="6" | "50" → feet="50", inches="0"
  //
  // Non-rectangular case:
  //   - frontFeet/frontInches → plotFrontWidth ya front ya frontWidth
  //   - backFeet/backInches   → plotBackWidth  ya back  ya backWidth  (fallback: width)
  //   - leftFeet/leftInches   → plotLeftDepth  ya left  ya leftDepth
  //   - rightFeet/rightInches → plotRightDepth ya right ya rightDepth (fallback: depth)
  //
  // NOTE: Old API me plotFrontWidth/plotBackWidth/plotLeftDepth/plotRightDepth 0 aata tha.
  //       New API me width aur depth se backWidth aur rightDepth bhi fill honge.
  //       Isliye: agar plotFrontWidth 0 ya missing hai to width ko front bhi maano.
  const nonRectFront = d.plotFrontWidth || d.frontWidth || d.front || widthRaw;
  const nonRectBack = d.plotBackWidth || d.backWidth || d.back || widthRaw;
  const nonRectLeft = d.plotLeftDepth || d.leftDepth || d.left || depthRaw;
  const nonRectRight = d.plotRightDepth || d.rightDepth || d.right || depthRaw;

  const frontParts = splitDecimal(nonRectFront);
  const backParts = splitDecimal(nonRectBack);
  const leftParts = splitDecimal(nonRectLeft);
  const rightParts = splitDecimal(nonRectRight);

  const sizeData = isIrregular
    ? {
      frontFeet: frontParts.feet,
      frontInches: frontParts.inches,
      backFeet: backParts.feet,
      backInches: backParts.inches,
      leftFeet: leftParts.feet,
      leftInches: leftParts.inches,
      rightFeet: rightParts.feet,
      rightInches: rightParts.inches,
      offset: d.plotOffset || d.offset || "0",
    }
    : {
      widthFeet: String(parseInt(d.plotWidth || d.width || "") || widthParts.feet),
      widthInches: widthParts.inches,
      depthFeet: String(parseInt(d.plotDepth || d.depth || "") || depthParts.feet),
      depthInches: depthParts.inches,
      // Irregular me jaane par prefill ke liye width/depth ko front/back/left/right me bhi rakhte hain
      frontFeet: widthParts.feet,
      frontInches: widthParts.inches,
      backFeet: backWidthParts.feet,
      backInches: backWidthParts.inches,
      leftFeet: depthParts.feet,
      leftInches: depthParts.inches,
      rightFeet: rightDepthParts.feet,
      rightInches: rightDepthParts.inches,
      offset: d.plotOffset || "0",
    };

  // FIX: Pehle flat keys se polygon banao — ab ye dono, saved aur reload,
  // exact SAME polygon banayenge kyunki toApiFormat ab actual coordinates
  // bhejta hai. Setbacks se reconstruct karna sirf legacy/old data ke liye
  // fallback hai (jab coordinates save hi nahi hue the).
  const buildingArea =
    flatKeysToPolygon(d) ||
    (
      (parseFloat(d.setbacksFront) > 0 || parseFloat(d.setbacksLeft) > 0)
        ? setbacksToPolygon(widthRaw, depthRaw, d.setbacksFront, d.setbacksBack, d.setbacksLeft, d.setbacksRight)
        : null
    );

  // FIX: diagonal — 0 ko empty string karo
  // Building area diagonal (Step7) — API dono keys bhej sakti hai: buildingAreaDiagonal1 ya diagonal1
  const diagonal1 = zeroToEmpty(d.buildingAreaDiagonal1 || d.diagonal1);
  const diagonal2 = zeroToEmpty(d.buildingAreaDiagonal2 || d.diagonal2);

  // Non-rectangular plot ke diagonals (BL→TR, BR→TL) — API keys
  const plotDiagonal1 = zeroToEmpty(d.plotDiagonal1);
  const plotDiagonal2 = zeroToEmpty(d.plotDiagonal2);

  const setbacks = {
    front: d.setbackFront || d.setbacksFront || "",
    back: d.setbackBack || d.setbacksBack || "",
    left: d.setbackLeft || d.setbacksLeft || "",
    right: d.setbackRight || d.setbacksRight || "",
  };

  // FIX: siteElements API se directly map karo
  const siteElements = {
    borewell: d.borewell || "",
    septicTank: d.septicTank || "",
    trees: d.trees || "",
    waterTank: d.waterTank || "",
  };

  // FIX: vastuPriority "yes"/"no"/"as_much_as_possible" — string check karo
  const vastuRaw = String(d.vastuPriority || "").toLowerCase();
  const vastuPreference =
    vastuRaw === "yes" || vastuRaw === "as_much_as_possible" || parseInt(d.vastuPriority) > 0
      ? vastuRaw || "yes"
      : "no";

  // FIX: plinthLevel, lintelLevel, sillLevel, slabHeight → feet+inches split
  const plinthParts = splitDecimal(d.plinthLevel || "");
  const lintelParts = splitDecimal(d.lintelLevel || "");
  const sillParts = splitDecimal(d.sillLevel || "");
  const slabHParts = splitDecimal(d.slabHeight || "");

  // FIX: levelDelta → levelFeet + levelInches
  const levelParts = splitDecimal(d.levelDelta || "");

  // FIX: slabExtension — 0 aata hai API se, "" chahiye component ko
  const slabExtension = zeroToEmpty(d.slabExtension);

  // FIX: slabSides — valid string ho tabhi set karo
  const slabSides = zeroToEmpty(d.slabSides);

  // FIX: attachmentUri — array ya string dono handle karo
  const attachmentUri = Array.isArray(d.attachmentUri)
    ? d.attachmentUri
    : d.attachmentUri
      ? [d.attachmentUri]
      : [];

  // referenceImage — array ya string dono handle karo (Step19 ki 2nd image)
  const referenceImage = Array.isArray(d.referenceImage)
    ? d.referenceImage
    : d.referenceImage
      ? [d.referenceImage]
      : [];

  // elevationLikedThemes — array normalize karo
  const elevationLikedThemes = Array.isArray(d.elevationLikedThemes)
    ? d.elevationLikedThemes
    : d.elevationLikedThemes
      ? [d.elevationLikedThemes]
      : [];

  return {
    shape: isIrregular ? "irregular" : "rectangular",
    direction,
    peripheries,
    roadWidthFeet: splitDecimal(d.roadWidth || "").feet,   // NEW — Step3
    roadWidthInches: splitDecimal(d.roadWidth || "").inches, // NEW — Step3
    size: sizeData,
    projectType: d.projectType || "residential",
    lifestyleType: d.lifestyleType || "",                   // NEW — Step6
    numberOfFloors,
    // NEW: API se aane wala naya flag — jab true ho, to Step6 me
    // numberOfFloors ka "prefilled/locked" state override ho jaata hai
    // aur user G se G+5 tak koi bhi floor select/update kar sakta hai.
    // false/missing hone par purana (locked) behavior hi chalega.
    canEditRsform: !!d.can_edit_rsform,
    floorsData,
    buildingArea,
    diagonal1,
    diagonal2,
    // Non-rectangular plot diagonals
    plotDiagonal1,
    plotDiagonal2,
    // Corner angles — API se aate hain toh restore karo (regular + irregular dono ke liye)
    cornerAngles: {
      BL: d.cornerAngleBL || "",
      BR: d.cornerAngleBR || "",
      TL: d.cornerAngleTL || "",
      TR: d.cornerAngleTR || "",
    },
    setbacks,
    parkingRequirement,
    stair,
    // FIX: stairType directly bhi set karo — kuch components direct stairType padhte hain
    stairType: stairTypeNormalized,
    siteElements,

    // FIX: wall thickness directly string mein
    externalWallThickness: d.externalWallThickness ? String(d.externalWallThickness) : "",
    internalWallThickness: d.internalWallThickness ? String(d.internalWallThickness) : "",

    // FIX: plinth/lintel/sill/slabHeight → feet+inches
    plinthLevelFeet: plinthParts.feet, plinthLevelInches: plinthParts.inches,
    lintelLevelFeet: lintelParts.feet, lintelLevelInches: lintelParts.inches,
    sillLevelFeet: sillParts.feet, sillLevelInches: sillParts.inches,
    slabHeightFeet: slabHParts.feet, slabHeightInches: slabHParts.inches,

    // FIX: levelDelta → levelFeet + levelInches
    levelRelation: d.levelRelation || "same",
    levelFeet: levelParts.feet,
    levelInches: levelParts.inches,

    // FIX: stairRiser/stairTread → riser/tread
    tread: d.stairTread ? String(d.stairTread) : "",
    riser: d.stairRiser ? String(d.stairRiser) : "",

    // FIX: slabSides/slabExtension — 0 ko "" treat karo
    slabSides,
    slabExtension,

    slab: { sides: "", slabExtension: "" },

    vastuPreference,

    // approximateBudget parse karo — "66 lakh" → {approximateBudget: "66", budgetUnit: "lakh"}
    ...(() => {
      const raw = d.approximateBudget || "";
      const match = raw.match(/^([\d.]+)\s*(thousand|lakh|crore)?$/i);
      return {
        approximateBudget: match ? match[1] : raw,
        budgetUnit: match && match[2] ? match[2].toLowerCase() : "crore",
      };
    })(),

    // FIX: attachmentUri array hai — attachPlotImage bhi accordingly set karo
    attachPlotImage: attachmentUri.length > 0 ? "yes" : (d.attachPlotImage || "no"),
    attachmentUri,
    referenceImage,

    // ── STRUCTURE module keys (pass-through — prefill ke liye) ────────────────
    structureCity: d.structureCity || "",
    seismicZone: d.seismicZone || "",
    seismicRisk: d.seismicRisk || "",
    soilType: d.soilType || "",
    soilCondition: d.soilCondition || "",
    footingType: d.footingType || "",
    structureStairType: d.structureStairType || "",

    // ── ELEVATION module keys (pass-through — prefill ke liye) ────────────────
    elevationTheme: d.elevationTheme || "",
    elevationLikedThemes,
    elevationGate: d.elevationGate || "",
    elevationPergola: d.elevationPergola || "",
    elevationStaircaseGlass: d.elevationStaircaseGlass || "",

    _meta: {
      projectId: apiData._projectId,
      status: apiData._status,
      version: apiData._version,
      updatedAt: apiData._updatedAt,
      address: d.Address || "",
      contactPerson: d.contactPerson || "",
      mobileNo: d.mobileNo || "",
      email: d.email || "",
      occupants: d.occupants || "",
      builtUpArea: d.builtUpArea || "",
      floorAreaRatio: d.floorAreaRatio || "",
      suitableTimeToCall: d.suitableTimetoCall || "",
    },
  };
};

// ─────────────────────────────────────────────────────────────────────────────
// flattenToFormData: nested object → formData[key] bracket notation
// ─────────────────────────────────────────────────────────────────────────────
const flattenToFormData = (obj) => {
  const flat = {};
  const recurse = (current, path) => {
    if (Array.isArray(current)) {
      current.forEach((item, idx) => recurse(item, `${path}[${idx}]`));
    } else if (current !== null && typeof current === "object") {
      Object.entries(current).forEach(([k, v]) => recurse(v, `${path}[${k}]`));
    } else if (current !== null && current !== undefined) {
      flat[path] = String(current);
    }
  };
  Object.entries(obj).forEach(([k, v]) => recurse(v, `formData[${k}]`));
  return flat;
};

// ─────────────────────────────────────────────────────────────────────────────
// toApiFormat: Convert new internal formData → flat formData[key] POST body
// ─────────────────────────────────────────────────────────────────────────────
export const toApiFormat = (formData) => {
  const f = formData;
  const size = f.size || {};

  // FIX: widthFeet/depthFeet mein decimal aa sakta hai purane data se
  // isliye split(".")[0] se sirf integer part lo
  const width = mergeToDecimal(
    String(size.widthFeet).split(".")[0],
    size.widthInches ?? "0"
  );
  const depth = mergeToDecimal(
    String(size.depthFeet).split(".")[0],
    size.depthInches ?? "0"
  );

  // Irregular plot size
  const irregularFront = mergeToDecimal(size.frontFeet, size.frontInches || "0");
  const irregularBack = mergeToDecimal(size.backFeet, size.backInches || "0");
  const irregularLeft = mergeToDecimal(size.leftFeet, size.leftInches || "0");
  const irregularRight = mergeToDecimal(size.rightFeet, size.rightInches || "0");

  const isIrregular = f.shape === "irregular" || f.shape === "non-rectangular";

  // Floor requirements: new floorsData → old requirementForfloors format
  const requirementForfloors = (f.floorsData || []).map((floor, idx) => {
    const spaces = floor.additionalSpaces || [];
    // kitchen → true/false, kitchenType → open/close (do alag keys)
    const kitchenOn = floor.kitchen === "yes";
    const kitchenType = floor.kitchenType === "open" ? "open" : "close";

    return {
      floorNo: idx,
      masterBedrooms: floor.masterBedrooms || "",
      masterBedroomsWithToilet: floor.masterBedroomsWithToilet || "0", // NEW — Step11
      bedroomsWithoutToilet: floor.bedroomsWithoutToilet || "0", // NEW — Step11
      attachedToilet: floor.withAttached === true ? "true" : "false",
      kitchen: kitchenOn ? "true" : "false",
      kitchenType: kitchenType,
      // commonAreaType removed — livingRoom/diningArea/drawingRoom as true/false instead
      livingRoom: spaces.includes("Living Room") ? "true" : "false",
      diningArea: spaces.includes("Dining Area") ? "true" : "false",
      drawingRoom: spaces.includes("Drawing Room") ? "true" : "false",
      poojaRoom: spaces.includes("Pooja Room") ? "true" : "false",
      washArea: spaces.includes("Utility/Wash Area") ? "true" : "false",
      dressingArea: spaces.includes("Dressing Area") ? "true" : "false",
      studyRoom: spaces.includes("Study Room") ? "true" : "false",
      gym: spaces.includes("Gym Room") ? "true" : "false",
      garden: spaces.includes("Garden") ? "true" : "false",
      lift: spaces.includes("Lift") ? "true" : "false",
      office: spaces.includes("Office") ? "true" : "false",
      foyer: spaces.includes("Foyer") ? "true" : "false",
      Balcony: spaces.includes("Balcony") ? "true" : "false",
      theatre: spaces.includes("Theatre Room") ? "true" : "false",
      recreationArea: spaces.includes("Recreation Area") ? "true" : "false",
      shop: spaces.includes("Shop") ? "true" : "false",
      AnySpecificRequirement: floor.otherSpaces || "",
    };
  });

  const nested = {
    direction: directionToCode(f.direction),
    floors: String(floorsToNumber(f.numberOfFloors)),
    shape: f.shape || "rectangular",

    // Plot dimensions — backend sirf ye 4 keys leta hai (rectangular/irregular dono).
    // POST (add) aur PUT (update) dono isi toApiFormat se jaate hain.
    //   width     = front side
    //   backWidth = back side
    //   leftDepth = left side
    //   depth     = right side
    // Rectangular me back=front (width) aur right=left (depth) auto-fill hota hai.
    // frontWidth/rightDepth/plotBackWidth/plotRightDepth ab nahi bhejte.
    ...((!isIrregular) && {
      width: width,
      backWidth: width,
      leftDepth: depth,
      depth: depth,
    }),

    ...(isIrregular && {
      width: irregularFront,
      backWidth: irregularBack,
      leftDepth: irregularLeft,
      depth: irregularRight,
    }),

    plotOffset: isIrregular ? String(size.offset || "0") : "0",
    plotDiagonal1: f.plotDiagonal1 || "",
    plotDiagonal2: f.plotDiagonal2 || "",
    cornerAngleBL: f.cornerAngles?.BL || (isIrregular ? "" : "90.0"),
    cornerAngleBR: f.cornerAngles?.BR || (isIrregular ? "" : "90.0"),
    cornerAngleTL: f.cornerAngles?.TL || (isIrregular ? "" : "90.0"),
    cornerAngleTR: f.cornerAngles?.TR || (isIrregular ? "" : "90.0"),

    inLeft: f.peripheries?.left || "",
    inRight: f.peripheries?.right || "",
    inBack: f.peripheries?.back || "",
    inFront: "Road",

    setbacksFront: f.setbacks?.front || "",
    setbacksBack: f.setbacks?.back || "",
    setbacksLeft: f.setbacks?.left || "",
    setbacksRight: f.setbacks?.right || "",

    parking: f.parkingRequirement || "",

    staircaseType: (() => {
      const t = f.stairType || f.stair?.type || "";
      return t ? t.charAt(0).toUpperCase() + t.slice(1) : "";
    })(),
    stairTread: f.tread || "",
    stairRiser: f.riser || "",

    levelRelation: f.levelRelation || "same",
    levelDelta: mergeToDecimal(f.levelFeet, f.levelInches),

    externalWallThickness: f.externalWallThickness || "",
    internalWallThickness: f.internalWallThickness || "",

    plinthLevel: mergeToDecimal(f.plinthLevelFeet, f.plinthLevelInches),
    lintelLevel: mergeToDecimal(f.lintelLevelFeet, f.lintelLevelInches),
    sillLevel: mergeToDecimal(f.sillLevelFeet, f.sillLevelInches),
    slabHeight: mergeToDecimal(f.slabHeightFeet, f.slabHeightInches),

    slabSides: f.slabSides || f.slab?.sides || "",
    slabExtension: f.slabExtension || f.slab?.slabExtension || "",

    borewell: f.siteElements?.borewell || "",
    septicTank: f.siteElements?.septicTank || "",
    trees: f.siteElements?.trees || "",
    waterTank: f.siteElements?.waterTank || "",

    requirementForfloors,

    // Building area diagonal
    buildingAreaDiagonal1: f.diagonal1 || "",
    buildingAreaDiagonal2: f.diagonal2 || "",
    diagonal1: f.diagonal1 || "",
    diagonal2: f.diagonal2 || "",

    // NEW — FIX: actual building-corner coordinates bhejo, taaki reload par
    // fromApiFormat exact wahi polygon restore kare jo Step7 me draw hua tha,
    // setbacks se lossy/galat reconstruction (dot-notation ko decimal maan lena)
    // ki zaroorat hi na pade. Sirf tab bhejo jab buildingArea poora 4-point
    // polygon ho (Elevation/Structure jaise modules me ye field hota hi nahi,
    // unke liye ye keys bilkul nahi jaayengi).
    ...(Array.isArray(f.buildingArea) && f.buildingArea.length === 4 && {
      buildingAreaTopLeftX: f.buildingArea[0]?.x,
      buildingAreaTopLeftY: f.buildingArea[0]?.y,
      buildingAreaTopRightX: f.buildingArea[1]?.x,
      buildingAreaTopRightY: f.buildingArea[1]?.y,
      buildingAreaBottomRightX: f.buildingArea[2]?.x,
      buildingAreaBottomRightY: f.buildingArea[2]?.y,
      buildingAreaBottomLeftX: f.buildingArea[3]?.x,
      buildingAreaBottomLeftY: f.buildingArea[3]?.y,
    }),

    // Corner gaps
    setbacksBackLeft: f.allGaps?.backLeft || "",
    setbacksBackRight: f.allGaps?.backRight || "",
    setbacksFrontLeft: f.allGaps?.frontLeft || "",
    setbacksFrontRight: f.allGaps?.frontRight || "",
    // Mid-edge gaps (same as setbacks.front/back/left/right — redundant but explicit)
    setbacksMidBack: f.allGaps?.midBack || f.setbacks?.back || "",
    setbacksMidFront: f.allGaps?.midFront || f.setbacks?.front || "",
    setbacksMidLeft: f.allGaps?.midLeft || f.setbacks?.left || "",
    setbacksMidRight: f.allGaps?.midRight || f.setbacks?.right || "",

    // NEW — Step3: road width
    roadWidth: (() => {
      const ft = parseFloat(f.roadWidthFeet) || 0;
      const inch = parseFloat(f.roadWidthInches) || 0;
      if (ft === 0 && inch === 0) return "";
      return inch > 0 ? `${ft}.${inch}` : `${ft}`;
    })(),

    // NEW — Step6: lifestyle type
    lifestyleType: f.lifestyleType || "",

    // NEW — Step7: area summary
    plotAreaSqFt: f.plotAreaSqFt || "",
    buildingAreaSqFt: f.buildingAreaSqFt || "",
    remainingAreaSqFt: f.remainingAreaSqFt || "",
    coveragePercent: f.coveragePercent || "",

    vastuPriority: f.vastuPreference || "",
    projectType: f.projectType || "residential",
    approximateBudget: f.approximateBudget
      ? `${f.approximateBudget} ${f.budgetUnit || "crore"}`
      : "",
    attachPlotImage: f.attachPlotImage || "no",
    attachmentUri: f.attachmentUri || "",
    referenceImage: f.referenceImage || "",
  };

  return flattenToFormData(nested);
};

// ─────────────────────────────────────────────────────────────────────────────
// toStructureApiFormat: STRUCTURE module ki sirf apni keys flatten karo
// Planning ki running keys ko touch nahi karna — isliye alag mapper.
// Body format same: formData[key]=value (x-www-form-urlencoded)
// ─────────────────────────────────────────────────────────────────────────────
export const toStructureApiFormat = (formData) => {
  const f = formData || {};

  const nested = {
    // Step 1 — Location & Seismic Zone
    structureCity: f.structureCity || "",
    seismicZone: f.seismicZone || "",
    seismicRisk: f.seismicRisk || "",

    // Step 2 — Soil Type
    soilType: f.soilType || "",
    soilCondition: f.soilCondition || "",

    // Step 3 — Footing Type
    footingType: f.footingType || "",

    // Step 4 — Floor Plan Heights (planning jaisi hi merged decimal format)
    plinthLevel: mergeToDecimal(f.plinthLevelFeet, f.plinthLevelInches),
    lintelLevel: mergeToDecimal(f.lintelLevelFeet, f.lintelLevelInches),
    sillLevel: mergeToDecimal(f.sillLevelFeet, f.sillLevelInches),
    slabHeight: mergeToDecimal(f.slabHeightFeet, f.slabHeightInches),

    // Step 5 — Stair Structure Type
    structureStairType: f.structureStairType || "",
  };

  return flattenToFormData(nested);
};

// ─────────────────────────────────────────────────────────────────────────────
// toElevationApiFormat: ELEVATION module —
// Planning ke shared steps (shape/direction/size/floors/heights/attachments)
// ki keys bilkul planning jaisi hi jaati hain (toApiFormat se — zero change),
// upar se elevation ki apni particular keys add ho jaati hain.
// ─────────────────────────────────────────────────────────────────────────────
export const toElevationApiFormat = (formData) => {
  const f = formData || {};

  // Planning shared keys — exactly same mapping as planning (no changes)
  const planningFlat = toApiFormat(f);

  // Elevation-specific keys
  const elevationNested = {
    elevationTheme: f.elevationTheme || "",
    elevationLikedThemes: Array.isArray(f.elevationLikedThemes) ? f.elevationLikedThemes : [],
    elevationGate: f.elevationGate || "",
    elevationPergola: f.elevationPergola || "",
    elevationStaircaseGlass: f.elevationStaircaseGlass || "",
    // Step19 ki 2nd image (reference image) — elevation me 2 images jaati hain
    referenceImage: f.referenceImage || "",
  };

  return { ...planningFlat, ...flattenToFormData(elevationNested) };
};

// ─────────────────────────────────────────────────────────────────────────────
// Determine API prefix based on who is accessing:
//   - Admin (token URL se) → "internal"
//   - Customer (OTP login) → "customer"
// ─────────────────────────────────────────────────────────────────────────────
const getApiPrefix = (updatedBy) => {
  if (updatedBy === "admin") return "internal";
  return "customer";
};

// ─────────────────────────────────────────────────────────────────────────────
// Customer token-expiry check — GET /customer/me
//
// Kaise kaam karta hai:
//   1. Stored customer token se /customer/me call karte hain.
//   2. Agar response ok nahi hai (401 waise hi jab token expire ho chuka ho)
//      → token invalid/expired maan lo.
//   3. Agar response ok hai → response ke "isd_code" aur "contact" ko
//      URL (?isdcode=..&mobile=..) ya localStorage ke saved isd_code/phone se
//      match karte hain, taki confirm ho ki yeh wahi user hai jiska token hai.
//   4. Match nahi hua ya token expire ho gaya → session clear karke false
//      return karte hain, taki caller login page pe redirect kar sake.
//
// NOTE: Yeh sirf naya check hai — kisi bhi existing function/flow ko
// modify nahi kiya gaya hai. Isko sirf wahi jagah use karo jaha
// customer login-expiry redirect handle karna hai.
// ─────────────────────────────────────────────────────────────────────────────
export const validateCustomerSession = async (token, isdCode, mobile) => {
  if (!token) {
    return { valid: false, reason: "no_token" };
  }

  try {
    const response = await fetch(`${API_BASE}/customer/me`, {
      method: "GET",
      headers: getHeaders(token),
    });

    if (!response.ok) {
      // Token expired ya invalid — data hi nahi aaya
      return { valid: false, reason: "expired" };
    }

    const data = await response.json();

    const apiIsd = String(data.isd_code ?? "").trim();
    const apiContact = String(data.contact ?? "").trim();
    const expectedIsd = String(isdCode ?? "").trim();
    const expectedMobile = String(mobile ?? "").trim();

    const isSameUser = apiIsd === expectedIsd && apiContact === expectedMobile;

    if (!isSameUser) {
      return { valid: false, reason: "mismatch", data };
    }

    return { valid: true, data };
  } catch (err) {
    console.error("validateCustomerSession failed:", err);
    return { valid: false, reason: "error" };
  }
};


export const checkCustomerTokenExpiry = async () => {
  const token = getCustomerToken();

  let isdCode = "";
  let mobile = "";
  try {
    const urlParams = new URLSearchParams(window.location.search);
    isdCode = urlParams.get("isdcode") || getUserIsdCode() || "";
    mobile = urlParams.get("mobile") || getUserPhone() || "";
  } catch (e) {
    isdCode = getUserIsdCode() || "";
    mobile = getUserPhone() || "";
  }

  const result = await validateCustomerSession(token, isdCode, mobile);

  if (!result.valid) {
    clearCustomerSession();
    return false;
  }

  // isd_code ko save kar lo agar pehli baar mila ho, aage kaam aayega
  if (isdCode) saveUserIsdCode(isdCode);

  return true;
};

// ─────────────────────────────────────────────────────────────────────────────
// GET: Fetch form data from API
// ─────────────────────────────────────────────────────────────────────────────
export const fetchFormData = async (projectId, token, updatedBy = "user", formVersion) => {
  const prefix = getApiPrefix(updatedBy);

  // FIX: agar caller ne formVersion pass nahi kiya (ya empty string aayi),
  // to URL ke query param "rsVersion" se fallback le lo — viewMode link me
  // version isi param se aata hai (…&rsVersion=1781523632).
  let versionToUse = formVersion;
  if (versionToUse === undefined || versionToUse === null || versionToUse === "") {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      versionToUse = urlParams.get("rsVersion") || "";
    } catch (e) {
      versionToUse = "";
    }
  }

  const response = await fetch(
    `${API_BASE}/${prefix}/form/${projectId}?version=${versionToUse}`,
    {
      method: "GET",
      headers: getHeaders(token),
    }
  );

  if (!response.ok) {
    throw new Error(`API ${response.status}: ${response.statusText}`);
  }

  const json = await response.json();

  const rawFormData = json.form_data || json;
  rawFormData._projectId = json.project_id;
  rawFormData._status = json.status;
  rawFormData._version = json.version;
  rawFormData._updatedAt = json.updatedat;
  // FIX: can_edit_rsform top-level response me aata hai (form_data ke andar
  // nahi) — isliye baaki _meta fields ki tarah yahin se copy karna zaroori
  // hai, warna fromApiFormat ke d.can_edit_rsform ko yeh kabhi nahi milega.
  rawFormData.can_edit_rsform = json.can_edit_rsform;

  return fromApiFormat(rawFormData);
};

// ─────────────────────────────────────────────────────────────────────────────
// moveCardToInProgress: Pehli baar step 1 pe aane pe call karo
// ─────────────────────────────────────────────────────────────────────────────
// NEW: ab URL me project "version" bhi jaata hai —
//   PUT /:prefix/form/moveCardToInProgress/:projectId/:version
// version optional param hai (4th arg). Agar caller ne version nahi diya
// (ya empty string), to purana bina-version wala URL hi hit hota hai —
// taaki is function ke existing (bina-version) callers kabhi break na ho.
export const moveCardToInProgress = async (projectId, token, updatedBy = "user", version) => {
  const prefix = getApiPrefix(updatedBy);
  try {
    const url = version
      ? `${API_BASE}/${prefix}/form/moveCardToInProgress/${projectId}/${version}`
      : `${API_BASE}/${prefix}/form/moveCardToInProgress/${projectId}`;

    const response = await fetch(url, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "x-api-key": token || "b1344b0057496b4c7bdc0e9b6ffcce6f",
      },
      body: JSON.stringify({}),
    });
    const data = await response.json();
    console.log(`📋 moveCardToInProgress (${prefix}) response:`, data);
    return data;
  } catch (err) {
    console.error(`moveCardToInProgress (${prefix}) failed:`, err);
  }
};

// ─────────────────────────────────────────────────────────────────────────────
// POST: Final submit
// ─────────────────────────────────────────────────────────────────────────────
export const postFormData = async (projectId, formData, updatedBy = "user", token, version) => {
  const prefix = getApiPrefix(updatedBy);
  const flat = toApiFormat(formData);

  flat["updatedBy"] = updatedBy;
  flat["updatedAt"] = new Date().toISOString();
  if (version !== undefined && version !== null && version !== "") {
    flat["version"] = String(version);
  }

  const body = Object.entries(flat)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");

  const postUrl = prefix === "internal"
    ? `${API_BASE}/${prefix}/form/store/${projectId}`
    : `${API_BASE}/${prefix}/form/${projectId}`;

  const response = await fetch(postUrl, {
    method: "POST",
    headers: {
      ...getHeaders(token),
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body,
  });

  if (!response.ok) {
    throw new Error(`API ${response.status}: ${response.statusText}`);
  }

  return response.json();
};

// ─────────────────────────────────────────────────────────────────────────────
// Shared sender for module-specific PUT/POST —
// same endpoints, same headers, same body format as planning:
//   PUT  /{prefix}/form/:projectId                  (step-by-step save)
//   POST /{prefix}/form/:projectId                  (customer final submit)
//   POST /{prefix}/form/store/:projectId            (internal final submit)
// ─────────────────────────────────────────────────────────────────────────────
const sendFlatFormData = async (method, projectId, flat, updatedBy, token, version) => {
  const prefix = getApiPrefix(updatedBy);

  flat["updatedBy"] = updatedBy;
  flat["updatedAt"] = new Date().toISOString();
  if (version !== undefined && version !== null && version !== "") {
    flat["version"] = String(version);
  }

  const body = Object.entries(flat)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");

  const url =
    method === "POST" && prefix === "internal"
      ? `${API_BASE}/${prefix}/form/store/${projectId}`
      : `${API_BASE}/${prefix}/form/${projectId}`;

  const response = await fetch(url, {
    method,
    headers: {
      ...getHeaders(token),
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body,
  });

  if (!response.ok) {
    throw new Error(`API ${response.status}: ${response.statusText}`);
  }

  return response.json();
};

// ─────────────────────────────────────────────────────────────────────────────
// STRUCTURE module — PUT (Next pe) / POST (final Submit pe)
// Sirf structure ki particular keys jaati hain — planning keys untouched.
// ─────────────────────────────────────────────────────────────────────────────
export const putStructureFormData = async (projectId, formData, updatedBy = "user", token, version) => {
  const flat = toStructureApiFormat(formData);
  return sendFlatFormData("PUT", projectId, flat, updatedBy, token, version);
};

export const postStructureFormData = async (projectId, formData, updatedBy = "user", token, version) => {
  const flat = toStructureApiFormat(formData);
  return sendFlatFormData("POST", projectId, flat, updatedBy, token, version);
};

// ─────────────────────────────────────────────────────────────────────────────
// ELEVATION module — PUT (Next pe) / POST (final Submit pe)
// Planning shared steps ki keys planning jaisi hi + elevation ki particular keys.
// ─────────────────────────────────────────────────────────────────────────────
export const putElevationFormData = async (projectId, formData, updatedBy = "user", token, version) => {
  const flat = toElevationApiFormat(formData);
  return sendFlatFormData("PUT", projectId, flat, updatedBy, token, version);
};

export const postElevationFormData = async (projectId, formData, updatedBy = "user", token, version) => {
  const flat = toElevationApiFormat(formData);
  return sendFlatFormData("POST", projectId, flat, updatedBy, token, version);
};
// ─────────────────────────────────────────────────────────────────────────────
// PUT: Step-by-step save
// ─────────────────────────────────────────────────────────────────────────────
export const putFormData = async (projectId, formData, updatedBy = "user", token, version) => {
  const prefix = getApiPrefix(updatedBy);
  const flat = toApiFormat(formData);

  flat["updatedBy"] = updatedBy;
  flat["updatedAt"] = new Date().toISOString();
  if (version !== undefined && version !== null && version !== "") {
    flat["version"] = String(version);
  }

  const body = Object.entries(flat)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");

  const response = await fetch(
    `${API_BASE}/${prefix}/form/${projectId}`,
    {
      method: "PUT",
      headers: {
        ...getHeaders(token),
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body,
    }
  );

  if (!response.ok) {
    throw new Error(`API ${response.status}: ${response.statusText}`);
  }

  return response.json();
};